import { CoteraAbilities } from '@cotera/api';
import { Main } from '@cotera/client/app/components/ui/main';
import { StateGetter, StateSetter, useAnalytics } from '@cotera/client/app/etc';
import { useLogger } from '@cotera/client/v0/actions/framework/logger';
import { Assert, assertOk } from '@cotera/utilities';
import { CircleX, TriangleAlert } from 'lucide-react';
import { useQueryClient, useSuspenseQueries } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { err, ok, Result } from 'neverthrow';
import React, { Suspense, useEffect, useMemo } from 'react';
import {
  Navigate,
  NavigateFunction,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useAuth } from '@workos-inc/authkit-react';
import { config } from '../config/config';
import { CommandPalette } from '../v0/actions/command-palette/command-palette';
import { useAction, useInScopeModel } from '../v0/actions/framework';
import { Actions, ModelScope } from '../v0/actions/framework/context';
import { DownloadCsvAction } from '../v0/data-grid/actions/download-csv.action';
import { OpenDetailViewAction } from '../v0/data-grid/actions/open-details.action';
import { OpenSettingsAction } from '../v0/data-grid/actions/open-settings.action';
import { ViewSqlAction } from '../v0/dataset/actions/view-sql.action';
import { V0Loading, V0Router, V0TabManager } from '../v0/v0';
import { Chip, Modal, Text } from './components';
import { NotFound } from './components/app/not-found';
import {
  ModalManager,
  ModalManagerProvider,
  useModalManager,
} from './components/app/modal-manager';
import { ChildrenProps } from './components/utils';
import {
  CoteraClientError,
  DevServerError,
  ManifestCompilerError,
  ManifestMeta,
  mkLocalDevServer,
  Org,
  Whoami,
} from './etc/api/typed-client';
import { ApiProvider } from './etc/data/api';
import { CoteraDuckDB } from './etc/duckdb';
import { SuspensefulStorageProvider } from './etc/storage/provider';
import { WorkerPool } from './etc/workers';
import {
  DevModeModel,
  DisableDevModeAction,
  EnableDevModeAction,
} from './features/dev-mode';
import { queryKeys } from './hooks/query-cache-keys';
import { useGetBearerToken } from './hooks/use-bearer-token';
import { useClient } from './hooks/use-client';
import { CurrentOrgProvider } from './hooks/use-current-org';
import { RuntimeDataProvider } from './runtime-data-provider';
import {
  AppDataProvider,
  useTenantedClient,
  useWhoami,
  WhoamiActions,
  WhoamiProvider,
  WhoamiState,
} from './stores/org';
import { useToolSyncEffect } from './hooks/use-tools-info';
import { useEagerQuery } from './hooks/use-eager-suspense-query';
import {
  getV1Override,
  SwitchAppVersionAction,
} from './v1/etc/switch-app-version.action';
import './v1/v1.css';
import { V1AppShell } from './v1/shell/v1-app';
import { EventsProvider } from './components/events-provider';
import { BroadcastEventType } from '@cotera/api';

const ORG_STORAGE_KEY = 'COTERA_SELECTED_ORG_ID';

const appDataActions = () => ({});

const getOrgAbilities = (whoami: Whoami, org: Org) =>
  CoteraAbilities.forRoles([
    ...whoami.untenantedRoles,
    ...whoami.tenantedRoles
      .filter((role) => role.orgId === org.id)
      .map(({ role }) => role),
  ]);

const whoamiActions =
  (navigate: NavigateFunction) =>
  (
    _set: StateSetter<WhoamiState>,
    get: StateGetter<WhoamiState>
  ): ReturnType<WhoamiActions> => {
    const changeOrg = (orgId: string) => {
      localStorage.setItem(ORG_STORAGE_KEY, orgId);
      const org = get().whoami.orgs.find((org) => org.id === orgId);
      if (!org) {
        return;
      }
      navigate(`/${org.slug}/`);
      window.location.reload();
    };
    return { changeOrg };
  };

export const RootRedirect: React.FC<{
  whoami: Whoami;
}> = ({ whoami }) => {
  const { organizationId: workosOrgId } = useAuth();

  // Priority: WorkOS org > localStorage > first org
  let org: Org | undefined;
  if (workosOrgId !== null) {
    org = whoami.orgs.find((o) => o.workosId === workosOrgId);
  }
  if (org === undefined) {
    const lsOrgId = localStorage.getItem(ORG_STORAGE_KEY);
    if (lsOrgId !== null) {
      org = whoami.orgs.find((o) => o.id === lsOrgId);
    }
  }
  org ??= whoami.orgs[0];

  if (org === undefined) {
    return <Navigate to="/login" />;
  }

  const search = window.location.search;
  const hash = window.location.hash;
  return <Navigate replace to={`/${org.slug}/${search}${hash}`} />;
};

export const OrgApp: React.FC<{
  whoami: Whoami;
  initedDb: Promise<CoteraDuckDB>;
}> = ({ whoami, initedDb }) => {
  const { orgName } = useParams();

  const org = whoami.orgs.find((o) => o.slug === orgName);

  if (org === undefined) {
    return <OrgNotFound />;
  }

  return (
    <CurrentOrgProvider value={org}>
      <OrgShell whoami={whoami} org={org} initedDb={initedDb} />
      <ImpersonationBorder whoami={whoami} />
    </CurrentOrgProvider>
  );
};

const ImpersonationBorder: React.FC<{ whoami: Whoami }> = ({ whoami }) => {
  if (whoami.impersonator === null) {
    return null;
  }
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[9999] border-4 border-orange-500"
      />
      <div className="pointer-events-none fixed bottom-6 right-6 z-[9999] rounded-full bg-orange-500 px-5 py-2 text-base font-semibold text-white shadow-lg">
        Impersonation mode
      </div>
    </>
  );
};

const OrgShell: React.FC<{
  whoami: Whoami;
  org: Org;
  initedDb: Promise<CoteraDuckDB>;
}> = ({ whoami, org, initedDb }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const api = useClient();
  const { identify } = useAnalytics();
  const {
    featureFlags,
    organizationId: workosOrgId,
    switchToOrganization,
  } = useAuth();

  // Identify the user to PostHog on every authenticated boot, regardless of
  // which app version (v1/Coco or classic v0) they land in. Onboarding only
  // identifies brand-new zero-org signups, so without this an invited user who
  // joins an existing org would never be linked to their PostHog person and
  // their chat events would land on an anonymous distinct_id.
  useEffect(() => {
    identify(org, whoami);
  }, [identify, org, whoami]);

  // Switch WorkOS session when the URL org doesn't match the current session org
  useEffect(() => {
    const isShareActivationRoute = location.pathname.startsWith(
      `/${org.slug}/share/`
    );
    if (isShareActivationRoute) {
      return;
    }
    if (
      org.workosId !== undefined &&
      org.workosId !== null &&
      (workosOrgId === null || org.workosId !== workosOrgId)
    ) {
      void switchToOrganization({ organizationId: org.workosId }).then(() => {
        window.location.reload();
      });
    }
  }, [
    location.pathname,
    org.slug,
    org.workosId,
    workosOrgId,
    switchToOrganization,
  ]);

  const localDevServer = useMemo(() => mkLocalDevServer(org.id), [org.id]);

  const abilities = getOrgAbilities(whoami, org);

  useEffect(() => {
    localStorage.setItem(ORG_STORAGE_KEY, org.id);
  }, [org.id]);

  const v1Override = getV1Override(whoami.id);
  // `legacy-app-access` grants the ability to use the classic Enterprise app
  // (it keeps the app-version switcher available). By default everyone lands
  // in Coco (v1); an org only defaults into classic if it also has the
  // `legacy-app-default` flag. Users can still switch either way, and that
  // explicit choice is honored below.
  const hasLegacyAccess = featureFlags?.includes('legacy-app-access') === true;
  const defaultsToLegacy =
    featureFlags?.includes('legacy-app-default') === true;

  let shouldUseClassic: boolean;
  if (!hasLegacyAccess) {
    // No legacy access → always Coco.
    shouldUseClassic = false;
  } else if (v1Override === 'enabled') {
    // User explicitly chose Coco.
    shouldUseClassic = false;
  } else if (v1Override === 'disabled') {
    // User explicitly chose the classic Enterprise experience.
    shouldUseClassic = true;
  } else {
    // No explicit choice yet → default to classic only if the org is flagged.
    shouldUseClassic = defaultsToLegacy;
  }
  const isV1 = !shouldUseClassic;

  return (
    <WhoamiProvider
      state={{ whoami, org, api, localDevServer, abilities }}
      actions={whoamiActions(navigate)}
    >
      {isV1 ? (
        <EventsProvider
          eventTypes={[
            BroadcastEventType.ChatActionUpdated,
            BroadcastEventType.ArtifactCreated,
            BroadcastEventType.CreditBalanceUpdated,
            BroadcastEventType.RecentChatsUpdated,
            BroadcastEventType.ResourceUpdated,
            BroadcastEventType.ChatBotConfigUpdated,
            BroadcastEventType.StackFrameWritten,
            BroadcastEventType.CliConnectionStatusChanged,
            // The server will not route an event type that is absent here.
            BroadcastEventType.RunWorkflow,
            BroadcastEventType.DatasetImport,
          ]}
        >
          <V1AppShell />
        </EventsProvider>
      ) : (
        <V0AppContent initedDb={initedDb} />
      )}
    </WhoamiProvider>
  );
};

const V0AppContent: React.FC<{
  initedDb: Promise<CoteraDuckDB>;
}> = ({ initedDb }) => {
  const org = useWhoami((s) => s.org);
  const whoami = useWhoami((s) => s.whoami);
  const abilities = useWhoami((s) => s.abilities);
  const api = useWhoami((s) => s.api);
  const localDevServer = useWhoami((s) => s.localDevServer);
  const logger = useLogger();
  const client = useTenantedClient();

  useEagerQuery(queryKeys.toolsV2.all({ orgId: org.id }), async () => {
    return assertOk(await client.toolsV2.list({}));
  });

  useEffect(() => {
    logger.setUser({
      id: whoami.id,
      email: whoami.email,
      username: whoami.name ?? undefined,
    });
  }, [whoami, logger]);

  return (
    <V0TabManager>
      <SuspensefulStorageProvider>
        <ModalManagerProvider>
          <AppActions abilities={abilities} db={initedDb} userId={whoami.id}>
            <ModelScope models={[new DevModeModel()]}>
              <ApiProvider api={api} localDevServer={localDevServer}>
                <Suspense fallback={<V0Loading />}>
                  <ParallelDataLoader initedDb={initedDb}>
                    <CommandPalette />
                    <V0Router />
                    <ModalManager />
                  </ParallelDataLoader>
                </Suspense>
              </ApiProvider>
            </ModelScope>
          </AppActions>
        </ModalManagerProvider>
      </SuspensefulStorageProvider>
    </V0TabManager>
  );
};

const DEV_MANIFEST_META_POLL_INTERVAL_MS = 2000; // 2 seconds
const COMMITS_POLL_INTERVAL_SECONDS = 5;

type ManifestMetaResult = Result<ManifestMeta, CoteraClientError>;

const manifestPool = new WorkerPool<
  {
    orgId: string;
    source: string;
    apiUrl: string;
    hash?: string;
    accessToken?: string;
  },
  | {
      data: ManifestMeta;
      t: 'data';
      hash: string;
    }
  | {
      error: DevServerError | ManifestCompilerError;
      t: 'error';
    }
  | {
      t: 'noop';
    }
>(
  () =>
    new Worker(new URL('./etc/data/skeleton.worker.ts', import.meta.url), {
      type: 'module',
    }),
  { poolSize: 1 }
);

/**
 * Hook that fetches all boot-time data in parallel using useSuspenseQueries.
 * - manifestMeta
 * - warehouse.getPrimary
 * - commits.list
 */
const useParallelBootData = () => {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const getBearerToken = useGetBearerToken();
  const model = useInScopeModel(DevModeModel)!;
  const devModeEnabled = useAtomValue(model.enabled.asAtom());
  const source = devModeEnabled ? 'dev' : 'commited';
  const manifestQueryKey = queryKeys.manifest.manifestMeta({ orgId, source });
  const warehouseQueryKey = queryKeys.warehouse.getPrimary({ orgId });
  const commitsQueryKey = ['commits', orgId] as const;

  const previousHash = queryClient.getQueryData<string>([
    ...manifestQueryKey,
    'hash',
  ]);
  const previousManifestResult =
    queryClient.getQueryData<ManifestMetaResult>(manifestQueryKey);

  const [{ data: manifestResult }, { data: wc }, { data: commits }] =
    useSuspenseQueries({
      queries: [
        {
          queryKey: manifestQueryKey,
          queryFn: async (): Promise<ManifestMetaResult> => {
            // Only get access token if we have a WorkOS session
            // For old cookie-based sessions, accessToken will be undefined
            const accessToken = await getBearerToken();
            // const accessToken = undefined;
            const res = await manifestPool.send({
              orgId,
              source,
              apiUrl: config.apiUrl,
              hash: previousHash,
              accessToken,
            });

            if (res.t === 'error') {
              switch (res.error.errorType) {
                case 'CompileError':
                case 'NoDevServerFound': {
                  return err(res.error);
                }
                default:
                  throw new Error(JSON.stringify(res.error));
              }
            } else if (res.t === 'noop') {
              return previousManifestResult!;
            } else {
              queryClient.setQueryData([...manifestQueryKey, 'hash'], res.hash);
              return ok(res.data);
            }
          },
          refetchInterval:
            source === 'dev' ? DEV_MANIFEST_META_POLL_INTERVAL_MS : false,
        },
        {
          queryKey: warehouseQueryKey,
          queryFn: async () => {
            return assertOk(await client.warehouse.getPrimary({}));
          },
        },
        {
          queryKey: commitsQueryKey,
          queryFn: async () => {
            const commits = Assert.assertOk(await client.commits.list({}));
            return commits;
          },
          refetchInterval: COMMITS_POLL_INTERVAL_SECONDS * 1000,
        },
        {
          queryKey: queryKeys.toolsV2.all({ orgId }),
          queryFn: async () => {
            return Assert.assertOk(await client.toolsV2.list({}));
          },
        },
      ],
    });

  useToolSyncEffect();

  return { manifestResult, wc, commits };
};

/**
 * Component that starts all data loading in parallel:
 * - manifestMeta
 * - warehouse.getPrimary
 * - commits.list
 *
 * All three queries start simultaneously after whoami completes.
 */
const ParallelDataLoader: React.FC<
  ChildrenProps & { initedDb: Promise<CoteraDuckDB> }
> = ({ children, initedDb: db }) => {
  const { manifestResult } = useParallelBootData();

  if (manifestResult.isErr()) {
    return (
      <DevServerErrorScreen
        error={manifestResult.error as unknown as DevServerError}
      />
    );
  }

  return (
    <AppDataProvider
      key={manifestResult.value.version}
      state={{ manifestMeta: manifestResult.value, initedDb: db }}
      actions={appDataActions}
    >
      <RuntimeDataProvider>{children}</RuntimeDataProvider>
    </AppDataProvider>
  );
};

const DevServerErrorScreen: React.FC<{
  error: DevServerError | ManifestCompilerError;
}> = ({ error }) => {
  const toggle = useAction(DisableDevModeAction);

  let inner: React.ReactNode;

  switch (error.errorType) {
    case 'CompileError':
      inner = (
        <pre className="text-left bg-accent-background w-full p-4 px-8 text-alt-text">
          {error.stackTrace}
        </pre>
      );
      break;
    case 'OrgNotFound':
    case 'NoDevServerFound':
      inner = null;
      break;
    default:
      return Assert.unreachable(error);
  }

  return (
    <Modal open priority="highest" onClose={() => {}} padding={false}>
      <div className="flex flex-col p-4 pt-6 min-w-[500px] items-center">
        <TriangleAlert className="text-rose-400 w-12 h-12" />
        <Text.Title
          type="section"
          className="flex items-center pb-2 text-alt-text"
        >
          Dev Server Error
        </Text.Title>
        <div className="flex items-center justify-center">
          <Chip type="negative" className="w-fit mr-4">
            {error.errorType}
          </Chip>
          <Text.Caption className="">
            For info on how dev mode works, click here.
          </Text.Caption>
        </div>
      </div>
      <div className="flex flex-col items-center text-center text-sm text-standard-text">
        {inner}
      </div>
      <button
        onClick={() => toggle.execute({})}
        className="focus:outline-hidden flex items-center justify-center focus:ring-0 bg-rose-400 px-3 py-3 w-full text-alt-text hover:bg-rose-500 text-sm"
      >
        <CircleX className="h-5 w-5 mr-2" /> Turn Off Dev Mode
      </button>
    </Modal>
  );
};

const AppActions: React.FC<{
  children: React.ReactNode;
  abilities: CoteraAbilities;
  db: Promise<CoteraDuckDB>;
  userId: string;
}> = ({ children, abilities, db, userId }) => {
  const modalManager = useModalManager((s) => s.actions);

  return (
    <Actions
      actions={[
        new DisableDevModeAction(abilities),
        new EnableDevModeAction(abilities),
        new OpenDetailViewAction(modalManager),
        new ViewSqlAction(modalManager),
        new DownloadCsvAction(db),
        new OpenSettingsAction(modalManager),
        new SwitchAppVersionAction(modalManager, 'classic', userId),
      ]}
    >
      {children}
    </Actions>
  );
};

const OrgNotFound: React.FC = () => (
  <Main>
    <NotFound resource="organization" />
  </Main>
);
